import { cn } from "@sameday/ui/cn";
import { type FC, memo, type ReactNode, use } from "react";
import { PageSpacingContext } from "./page-spacing-context";

type PageContentProps = {
  children: ReactNode;
  className?: string;
  variant?: "default" | "dashboard";
};

/**
 * Page flex chain and optional reading-width limit. The app shell owns padding.
 * Standalone uses (such as a route error outside the shell) get the same inset
 * here; nested PageContent wrappers never add a second inset.
 *
 * Scrolls with `<main>` by default. A page that owns its own scroll regions
 * instead passes `h-full`: `<main>` is a flex item with a definite height, so
 * `100%` (and the `flex-1 min-h-0` chain below it) resolves against the real
 * box. Never size a page with `100dvh - var(--nav-height)` — that is only right
 * while the shell's chrome is exactly that tall, and overflows `<main>` by the
 * difference when a banner is up (SD-16854).
 */
export const PageContent: FC<PageContentProps> = memo(
  ({ children, className, variant = "default" }) => {
    const spacingOwned = use(PageSpacingContext);
    return (
      <PageSpacingContext value={true}>
        <div
          className={cn(
            "flex-1 min-h-0 flex flex-col",
            !spacingOwned && "app-page-padding",
            className,
          )}
        >
          <div
            className={cn(
              "flex flex-1 min-h-0",
              variant === "dashboard" && "mx-auto w-full max-w-5xl",
            )}
          >
            <div className="flex-1 min-w-0 min-h-0 flex flex-col">
              {children}
            </div>
          </div>
        </div>
      </PageSpacingContext>
    );
  },
);

PageContent.displayName = "PageContent";
